import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import AssessmentsUploadDropzone from "./AssessmentsUploadDropzone";
import AssessmentsUploadFileRow from "./AssessmentsUploadFileRow";
import LiveRegion from "../../../components/LiveRegion/LiveRegion";
import useAssessmentsUpload from "./useAssessmentsUpload";
import { getAssessmentRelationPreposition } from "./AssessmentsUploadModal.utils";
import { toastSuccess, toastError } from "../../../utils";
import { createAssessment } from "../../../services/api/cultureHub/cultureHub.api";

const TITLE_ID = "assessments-upload-title";
const RELATION_ID = "assessments-upload-relation";
const FILES_HEADING_ID = "assessments-upload-files-heading";

const AssessmentsUploadModal = ({
  open,
  onClose,
  onSuccess,
  relationLabel,
  attachmentIds,
}) => {
  const {
    files,
    addFiles,
    removeFile,
    reset,
    markFilesAsSaved,
    cancelPending,
    isUploading,
    hasCompletedFile,
    assessmentTypeId,
  } = useAssessmentsUpload();
  const [isSaving, setIsSaving] = useState(false);
  const saveSourceRef = useRef(null);

  const cancelSave = () => {
    if (saveSourceRef.current) {
      saveSourceRef.current.cancel();
      saveSourceRef.current = null;
    }
  };

  // unmount (e.g. the parent dialog closing) while a save is in flight
  useEffect(() => cancelSave, []);

  const handleDiscard = () => {
    cancelPending();
    cancelSave();
    setIsSaving(false);
    reset();
    onClose();
  };

  const handleSave = () => {
    const pendingFiles = files.filter((file) => file.isSent && !file.isSaved);
    const source = axios.CancelToken.source();

    saveSourceRef.current = source;
    setIsSaving(true);

    Promise.allSettled(
      pendingFiles.map((file) =>
        createAssessment({
          fileId: file.fileId,
          assessmentTypeId,
          attachmentIds,
          cancelToken: source.token,
        }).then(() => file.id)
      )
    )
      .then((results) => {
        if (saveSourceRef.current !== source) return;

        const savedIds = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);
        const failedCount = results.length - savedIds.length;

        if (savedIds.length > 0) {
          markFilesAsSaved(savedIds);
        }

        if (failedCount > 0) {
          toastError(
            failedCount > 1
              ? `Não foi possível salvar ${failedCount} pareceres`
              : "Não foi possível salvar o parecer"
          );
          return;
        }

        if (pendingFiles.length > 1) {
          toastSuccess("Pareceres enviados com sucesso");
        } else {
          toastSuccess("Parecer enviado com sucesso");
        }
        if (onSuccess) onSuccess();
        reset();
        onClose();
      })
      .finally(() => {
        if (saveSourceRef.current !== source) return;

        saveSourceRef.current = null;
        setIsSaving(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleDiscard}
      fullWidth
      maxWidth="sm"
      aria-labelledby={TITLE_ID}
      aria-describedby={relationLabel ? RELATION_ID : undefined}
      BackdropProps={{
        sx: { backgroundColor: "rgba(61, 61, 61, 0.3)" },
      }}
      PaperProps={{
        "aria-modal": true,
        sx: {
          borderRadius: "8px",
          p: 3,
        },
      }}
    >
      <LiveRegion />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              component="h2"
              id={TITLE_ID}
              sx={{
                fontSize: "22px",
                fontWeight: 600,
                color: "#3D3D3D",
              }}
            >
              Upload de parecer
            </Typography>

            {!!relationLabel && (
              <Typography
                id={RELATION_ID}
                sx={{
                  mt: 0.5,
                  fontSize: "14px",
                  color: "#666666",
                }}
              >
                O parecer vai ser relacionado{" "}
                {getAssessmentRelationPreposition(relationLabel)}{" "}
                {relationLabel}
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={handleDiscard}
            size="small"
            aria-label="Fechar upload de parecer"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <AssessmentsUploadDropzone onAddFiles={addFiles} />

        {files.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography
              component="h3"
              id={FILES_HEADING_ID}
              sx={{ fontSize: "14px", fontWeight: 400, color: "#646464" }}
            >
              Arquivos enviados
            </Typography>

            <Box
              component="ul"
              aria-labelledby={FILES_HEADING_ID}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                listStyle: "none",
                m: 0,
                p: 0,
              }}
            >
              {files.map((file) => (
                <AssessmentsUploadFileRow
                  key={file.id}
                  file={file}
                  onRemove={removeFile}
                />
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleDiscard}
            disabled={isSaving}
            sx={{ textTransform: "none", color: "#185CFB", borderColor: "#E2E2E3" }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !hasCompletedFile || isUploading || isSaving || !assessmentTypeId
            }
            startIcon={
              isSaving ? (
                <CircularProgress size={16} thickness={4} sx={{ color: "inherit" }} />
              ) : undefined
            }
            sx={{
              textTransform: "none",
              bgcolor: "#185CFB",
              "&:hover": { bgcolor: "#185CFB" },
            }}
          >
            {isSaving ? "Salvando" : "Salvar"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AssessmentsUploadModal;
